import * as React from "react"
import autobind from "class-autobind"
import FloatingActionButton from "material-ui/FloatingActionButton"
import ContentAdd from "material-ui/svg-icons/content/add"
import Table from "../../../components/common/Table"
import { TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn, SelectField, MenuItem } from "material-ui"
import { green500 } from "material-ui/styles/colors"
import TableActionBar from "../../../components/layout/TableActionBar"
import AddUserDetailAccrual from "./AddUserDetailAccrual"
import UserStore from "../../../stores/UserStore"
import { IEmployeeAccrual, IUserDocument, EAccrualType } from "../../../models"
import strings from "../../../localization/strings"
import moment from "moment"
import UserAccrualAuditDialog from "./UserAccrualAuditDialog"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as userActions from "../../../actions/UserActions"
import AppStateStore from "../../../stores/AppStateStore"
import AccountStore from "../../../stores/AccountStore"
import * as utils from "../../../helpers/utils"
import UserDetailAccrualsReport from "./UserDetailAccrualsReport"
import * as timeHelper from "../../../helpers/timeHelper"

interface IProps {
	user: IUserDocument
	readonly: boolean
}
interface IState {
	accruals: IEmployeeAccrual[]
	openAddAccrual: boolean
	editAccrual: IEmployeeAccrual
	selectedAccrual: IEmployeeAccrual
	searchText: string
	accrualForAduitsDialog: IEmployeeAccrual
	showActiveAccrual: boolean
}
const styles = {
	columnValue: { textAlign: "center", cursor: "pointer", whitespace: "normal", paddingLeft: 10, paddingRight: 10 },
	accrualProbationEndDateColumnValue: { textAlign: "center", width: 170, paddingLeft: 0, paddingRight: 0, cursor: "pointer", whitespace: "normal" },
	useForLeaveTrackingColumnValue: { textAlign: "center", width: 150, paddingLeft: 0, paddingRight: 0, cursor: "pointer", whitespace: "normal" },
	auditColumnValue: { textAlign: "center", width: 70, paddingLeft: 0, paddingRight: 0, cursor: "pointer", whitespace: "normal" },
	noAccrulas: { textAlign: "center" }
}

export default class UserDetailAccruals extends React.Component<IProps, IState> {
	constructor(props) {
		super(props)
		this.state = {
			accruals: [],
			openAddAccrual: false,
			editAccrual: null,
			selectedAccrual: null,
			searchText: "",
			accrualForAduitsDialog: null,
			showActiveAccrual: true,
		}
		autobind(this)
	}

	componentDidMount() {
		this.userChangeEvent()
		UserStore.on("change", this.userChangeEvent)
	}

	componentWillUnmount() {
		UserStore.removeListener("change", this.userChangeEvent)
	}

	userChangeEvent() {
		this.setState({
			accruals: UserStore.getCurrentUserDetail().Accruals,
			selectedAccrual: null,
			editAccrual: null,
			searchText: "",
		})
	}

	handleAdd(accrual: IEmployeeAccrual) {
		AppStateStore.setProgressSnackbarContent("loading")
		this.setState({ openAddAccrual: false, editAccrual: null })
		userActions.addEmployeeAccrual(this.props.user.Id, accrual, () =>
			AppStateStore.setProgressSnackbarContent(strings.Employee_accrual_added_successfully)
		)
	}

	handleUpdate(accrual: IEmployeeAccrual) {
		AppStateStore.setProgressSnackbarContent("loading")
		this.setState({ openAddAccrual: false, editAccrual: null })
		if (AccountStore.getAccrualManualAdjustmentEnabled()) {
			userActions.updateManualEmployeeAccrual(this.props.user.Id, accrual, () =>
				AppStateStore.setProgressSnackbarContent(strings.Employee_accrual_updated_successfully)
			)
				.catch(err => AppStateStore.setProgressSnackbarContent(err.response.data.message))
		}
		else {
			userActions.updateEmployeeAccrual(this.props.user.Id, accrual, () =>
				AppStateStore.setProgressSnackbarContent(strings.Employee_accrual_updated_successfully)
			)
		}
	}

	onAuditClick(accrual: IEmployeeAccrual) {
		this.setState({ accrualForAduitsDialog: accrual })
	}

	handleDelete(accrual: IEmployeeAccrual) {
		AppStateStore.setProgressSnackbarContent("loading")
		userActions.deleteEmployeeAccrual(this.props.user.Id, accrual.Id,
			deleteResult => {
				AppStateStore.setProgressSnackbarContent(deleteResult ? strings.Employee_accrual_removed_successfully : strings.Cannot_delete_Accrual_which_is_referred_in_Leave_Request)
				this.setState({ selectedAccrual: null })
			}
		)
	}

	getUnit(accrual: IEmployeeAccrual) {
		const accountAccrual = AccountStore.getCurrentAccount().Accruals.find(i => i.Id === accrual.LinkedAccountAccrualId)
		if (accountAccrual.Type === EAccrualType.Monetary) {
			return "$"
		}

		return strings.Hours.toLowerCase()
	}

	render() {
		let filteredAccruals = this.state.accruals.filter(i => this.state.showActiveAccrual ? i.Active : !i.Active)
		const filterKey = (this.state.searchText || "").toLowerCase()
		if (filterKey) {
			filteredAccruals = filteredAccruals.filter(i =>
				i.Name.toLowerCase().includes(filterKey) ||
				String(i.Balance).toLowerCase().includes(filterKey) ||
				String(i.StartingBalance).toLowerCase().includes(filterKey) ||
				i.AccrualStartDate.toLowerCase().includes(filterKey) ||
				(i.AccrualEndProbationDate ?
					i.AccrualEndProbationDate.toLowerCase().includes(filterKey) :
					false
				)
			)
		}

		return (
			<div style={{ position: "relative" }}>
				{!this.props.readonly && this.state.showActiveAccrual &&
					permissionHelper.hasPermission(AuthenticationStore.Permissions.Accrual.AddUserAccrual) &&
					<FloatingActionButton
						data-intercom-target={"menu-Employees-Accruals-Btn-Add"}
						style={{ position: "absolute", float: "right", right: 50, top: -45 }}
						secondary={true}
						onClick={() => this.setState({ openAddAccrual: true, editAccrual: null })}
					>
						<ContentAdd />
					</FloatingActionButton>
				}
				<div>
					<TableActionBar
						onSearch={searchText => this.setState({ searchText })}
						searchText={this.state.searchText}
						rowSelectionCount={this.state.selectedAccrual ? 1 : 0}
						singleSelectedMessage={strings.Accrual_Selected}
						title={strings.Accruals}
						onDeleteClick={() =>
							AppStateStore.setConfirmDialogContent({
								content: strings.Are_you_sure_delete_accrual,
								confirm: () => this.handleDelete(this.state.selectedAccrual)
							})
						}
						customActionElement={
							<UserDetailAccrualsReport
								user={this.props.user}
							/>
						}
					/>
					<div style={{ display: "flex", justifyContent: "flex-end" }}>
						<SelectField
							data-intercom-target={"Employee-Accrual-Select-Status"}
							floatingLabelText={strings.Active_Status}
							value={this.state.showActiveAccrual}
							onChange={(event, key, value) => this.setState({ showActiveAccrual: value, selectedAccrual: null })}
							style={{ marginLeft: 35, marginRight: 35, width: 250 }}
						>
							<MenuItem value={true} primaryText={strings.Active} />
							<MenuItem value={false} primaryText={strings.Inactive} />
						</SelectField>
					</div>
					<div>
						{filteredAccruals.length === 0 ?
							<h2 style={styles.noAccrulas}>{strings.No_Accrual}</h2> :
							<Table
								singleSelectable
								items={filteredAccruals}
								selectedItems={this.state.selectedAccrual ? [this.state.selectedAccrual] : []}
								bodyStyle={{ height: filteredAccruals.length === 0 ? 0 : "calc(100vh - 400px)", overflowY: "visible" }}
								handleSelect={(!this.props.readonly && permissionHelper.hasPermission(AuthenticationStore.Permissions.Accrual.DeleteUserAccrual)) ?
									selectedAccruals => this.setState({ selectedAccrual: selectedAccruals[0] }) :
									null
								}
								handleClickRow={(!this.props.readonly && permissionHelper.hasPermission(AuthenticationStore.Permissions.Accrual.EditUserAccrual)) ?
									editAccrual => this.setState({ openAddAccrual: true, editAccrual }) :
									null
								}
								readonly={this.props.readonly}
							>
								<TableHeader>
									<TableRow>
										<TableHeaderColumn className="TextOverflow" title={strings.Accrual_Name} style={styles.columnValue}>{strings.Accrual_Name}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Accrual_Type} style={styles.columnValue}>{strings.Accrual_Type}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Starting_Balance} style={styles.columnValue}>{strings.Starting_Balance}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Current_Balance} style={styles.columnValue}>{strings.Current_Balance}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Pending_Balance} style={styles.columnValue}>{strings.Pending_Balance}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Start_Date} style={styles.columnValue}>{strings.Start_Date}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Probation_End_Date} style={styles.accrualProbationEndDateColumnValue}>{strings.Probation_End_Date}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Use_for_Leave_Tracking} style={styles.useForLeaveTrackingColumnValue}>{strings.Use_for_Leave_Tracking}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Audit} style={styles.auditColumnValue}>{strings.Audit}</TableHeaderColumn>
										<TableHeaderColumn className="TextOverflow" title={strings.Active} style={styles.auditColumnValue}>{strings.Active}</TableHeaderColumn>
									</TableRow>
								</TableHeader>
								<TableBody deselectOnClickaway={false}>
									{filteredAccruals.map((accrual, key) =>
										<TableRow
											key={key}
											data-intercom-target={key === 0 && "menu-EmployeeAccruals-openEmployeeAccrualDetails"}
											selected={this.state.selectedAccrual && this.state.selectedAccrual.Id === accrual.Id ? true : false}
										>
											<TableRowColumn style={styles.columnValue}><span title={accrual.Name}>{accrual.Name}</span></TableRowColumn>
											<TableRowColumn style={styles.columnValue}>
												{(() => {
													const accountAccrual = AccountStore.getCurrentAccount().Accruals.find(i => i.Id === accrual.LinkedAccountAccrualId)
													if (accountAccrual.Type === EAccrualType.Monetary) {
														return "Monetary"
													}

													return accountAccrual.Type === EAccrualType.PerHourWorked ? strings.Per_Hour_Worked : strings.Per_Day
												})()}
											</TableRowColumn>
											<TableRowColumn style={styles.columnValue}>{`${utils.roundToTwo(accrual.StartingBalance)} ${this.getUnit(accrual)}`}</TableRowColumn>
											<TableRowColumn style={styles.columnValue}>{`${utils.roundToTwo(accrual.Balance)} ${this.getUnit(accrual)}`}</TableRowColumn>
											<TableRowColumn style={styles.columnValue}>{`${utils.roundToTwo(accrual.PendingBalance)} ${this.getUnit(accrual)}`}</TableRowColumn>
											<TableRowColumn style={styles.columnValue}>{moment(timeHelper.keepConvertUTCToLocal(accrual.AccrualStartDate)).format("MM/DD/YYYY")}</TableRowColumn>
											<TableRowColumn style={styles.accrualProbationEndDateColumnValue}>
												{accrual.AllowUseForLeaveTracking && accrual.EnableProbationDate &&
													moment(timeHelper.keepConvertUTCToLocal(accrual.AccrualEndProbationDate)).format("MM/DD/YYYY")
												}
											</TableRowColumn>
											<TableRowColumn style={styles.useForLeaveTrackingColumnValue}>
												{accrual.AllowUseForLeaveTracking && <div><i style={{ fontSize: 22, color: green500 }} className="fa fa-check" /></div>}
											</TableRowColumn>
											<TableRowColumn style={styles.auditColumnValue}>
												<div onClick={e => e.stopPropagation()}>
													<i
														data-intercom-target={key === 0 && "menu-Employees-Accruals-Btn-OpenAccrualAuditDialog"}
														className="fa fa-info-circle"
														style={{ marginRight: 5, color: "#aaaaaa", cursor: "pointer", display: "inline-block" }}
														onClick={() => this.onAuditClick(accrual)}
													/>
												</div>
											</TableRowColumn>
											<TableRowColumn style={styles.auditColumnValue}>
												{accrual.Active && <div><i style={{ fontSize: 22, color: green500 }} className="fa fa-check" /></div>}
											</TableRowColumn>
										</TableRow>
									)}
								</TableBody>
							</Table>
						}
					</div>
				</div>
				{this.state.openAddAccrual &&
					<AddUserDetailAccrual
						user={this.props.user}
						existingAccruals={this.state.accruals}
						editAccrual={this.state.editAccrual}
						handleClose={() => this.setState({ openAddAccrual: false, editAccrual: null })}
						handleAdd={this.handleAdd}
						handleUpdate={this.handleUpdate}
					/>
				}
				{this.state.accrualForAduitsDialog &&
					<UserAccrualAuditDialog
						employeeName={`${this.props.user.FirstName} ${this.props.user.LastName}`}
						accrualName={this.state.accrualForAduitsDialog.Name}
						audits={this.state.accrualForAduitsDialog.ChangeAudits}
						handleClose={() => this.setState({ accrualForAduitsDialog: null })}
					/>
				}
			</div >
		)
	}
}
